import { Head } from "@shared/components/head";
import { Text } from "@shared/components/text";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getSpeechLocale } from "@shared/i18n/languages";
import { TouchableOpacity, View, Keyboard } from "react-native";
import {
  QUESTIONNAIRE_SINGLE_SELECT_ANSWER_TYPES,
  QuestionnaireAnswer,
  QuestionnaireStepQuestion,
} from "app/_modules/core/questionnaire";
import { SingleChoiceOptions } from "./questionnaire/SingleChoiceOptions";
import { MultipleChoiceOptions } from "./questionnaire/MultipleChoiceOptions";
import { FreeTextInput } from "./questionnaire/FreeTextInput";

interface QuestionnaireSubmitFormProps {
  question: QuestionnaireStepQuestion;
  answer?: QuestionnaireAnswer;
  showError?: boolean;
  onAnswerChange: (answer: QuestionnaireAnswer) => void;
}

export const QuestionnaireSubmitForm = ({ question, answer, showError, onAnswerChange }: QuestionnaireSubmitFormProps) => {
  const { t, i18n } = useTranslation();

  const voiceLocale = getSpeechLocale(i18n.language);

  const voiceMessages = useMemo(() => ({
    permissionDenied: t('screens.activities.questionnaire.dictationPermissionDenied'),
    notAvailable: t('screens.activities.questionnaire.dictationNotAvailable'),
    error: t('screens.activities.questionnaire.dictationError'),
  }), [t]);

  const orderedOptions = useMemo(
    () => question.options.map((option) => ({ ...option, label: t(option.label, option.label) })),
    [question.options, t]
  );

  const handleSingleChoiceSelect = (value: string, label: string) => {
    const isSelected = answer?.value === value;
    onAnswerChange(isSelected
      ? { value: null, text: null, otherText: null }
      : { value, text: label, otherText: null }
    );
  };

  const handleFreeTextChange = (value: string) => {
    onAnswerChange({ value, text: value, otherText: null });
  };

  const renderAnswerInput = () => {
    if (QUESTIONNAIRE_SINGLE_SELECT_ANSWER_TYPES.includes(question.answer_type)) {
      return (
        <SingleChoiceOptions
          orderedOptions={orderedOptions}
          selectedValue={typeof answer?.value === 'string' ? answer.value : null}
          onSelect={handleSingleChoiceSelect}
        />
      );
    }

    if (question.answer_type === 'multiple_choice') {
      return (
        <MultipleChoiceOptions
          orderedOptions={orderedOptions}
          answer={answer}
          onAnswerChange={onAnswerChange}
          voiceLocale={voiceLocale}
          voiceMessages={voiceMessages}
        />
      );
    }

    if (question.answer_type === 'free_text') {
      return (
        <FreeTextInput
          value={typeof answer?.value === 'string' ? answer.value : undefined}
          onChangeText={handleFreeTextChange}
          voiceLocale={voiceLocale}
          voiceMessages={voiceMessages}
          showError={showError && question.mandatory}
        />
      );
    }

    return null;
  };

  return (
    <TouchableOpacity
      activeOpacity={1}
      onPress={() => Keyboard.dismiss()}
      className="flex-1"
    >
      <View className="flex-1 flex-col justify-between">
        <View className="flex-col gap-6">
          <Head type="h2">{question.text || t("screens.activities.questionnaire.header")}</Head>

          {question.mandatory && (
            <Text>{t("screens.activities.questionnaire.requiredAnswer")}</Text>
          )}

          <View className="flex-col gap-4 bg-white pt-2">
            {renderAnswerInput()}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};
